<template>
  <section
    class="flex min-w-0 max-w-full items-center rounded-8 p-2 gap-3 cursor-pointer hover:bg-bg-base-bg3 group transition-all"
    @mouseenter="show"
    @mouseleave="hide"
  >
    <contact-avatar
      class="flex-none"
      :name="name"
      contact-type="pstnGuest"
    />

    <div class="grid min-w-0 grow items-center grid-rows-2 gap-1 overflow-hidden">
      <p class="text-heading-m-16-22 text-fg-base-fg3 truncate">
        {{ name }}
      </p>
      <div class="flex min-w-0 items-center gap-x-2 overflow-hidden text-label-r-13-18 text-fg-base-fg22">
        <wui-icon
          :name="iconName"
          :class="iconColor"
          class="flex-none shrink-0"
          small
        />
        <p class="min-w-0 truncate tracking-[0.4px]">
          {{ number }}
        </p>
      </div>
    </div>

    <div class="relative flex flex-none shrink-0 items-center justify-end w-24 h-11">
      <div
        class="text-label-r-13-18 text-fg-base-fg22 absolute inset-0 grid grid-rows-2 content-center justify-items-end gap-y-2 overflow-hidden transition-opacity"
        :class="{ 'opacity-0 invisible': hover && !callInProgress }"
      >
        <p class="min-w-0 max-w-full truncate whitespace-nowrap">
          {{ date }}
        </p>
        <p
          v-if="eventItem?.event === 'call_ended' || eventItem?.event === 'call_forwarded'"
          class="min-w-0 max-w-full truncate whitespace-nowrap tracking-[0.4px]"
        >
          {{ duration }}
        </p>
      </div>
      <wui-btn
        v-if="webSoftphoneEnabled && hover && !callInProgress"
        class="relative z-10"
        :title="$t('common.makeCall')"
        :aria-label="$t('common.makeCall')"
        variant="brand"
        state="tonal"
        icon
        auto-loading
        :disabled="isCalling"
        :prepend-icon="isCalling ? 'spinner' : 'phoneM'"
        @click.stop="onCallClick()"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { IconName, WuiIcon, WuiBtn, useRenderNotifier } from '@wui/common-library'
import { computed, ref } from 'vue'


import { ContactAvatar } from '@/entities/contact-avatar'
import { callFromAnotherTerminalIfNeeded } from '@/entities/user-terminal'
import { useWebRTC } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { getMicrophonePermissionErrorMessage } from '@/shared/composables/media-stream'
import { useHover } from '@/shared/composables/use-hover'
import { useWebSoftphoneEnabled } from '@/shared/composables/user-features'
import { getOfferOptions } from '@/shared/utils/jssip'

import { getCallForwardedDisplayLabel, isCallForwardedEvent } from '../lib'
import type { ExternalCall } from '../types'

const props = defineProps<{
  number: string
  iconName: IconName
  iconColor: string
  date: string
  duration: string
  callInProgress: boolean
  eventItem: ExternalCall
}>()

const { hover, show, hide } = useHover()
const { webSoftphoneEnabled } = useWebSoftphoneEnabled()
const { callAsync } = useWebRTC()
const { currentUser } = useCurrentUser()
const { notifyError } = useRenderNotifier()
const isCalling = ref(false)

const isOutgoing = computed(() => props.eventItem?.srcPServed === currentUser.value?.imLogin)

const onCallClick = async () => {
  if (isCalling.value) {
    return
  }

  isCalling.value = true
  try {
    if (await callFromAnotherTerminalIfNeeded(props.number)) {
      return
    }

    const options = await getOfferOptions(true, false)
    await callAsync(props.number, options).catch(() => undefined)
  } catch (e) {
    const errorMessage = getMicrophonePermissionErrorMessage(e)
    notifyError(errorMessage)
  } finally {
    isCalling.value = false
  }
}

const name = computed(() => {
  const item = props.eventItem
  if (isCallForwardedEvent(item)) {
    return getCallForwardedDisplayLabel(item, undefined, currentUser.value?.imLogin) || props.number
  }
  if (isOutgoing.value) {
    const dst = 'dstNumber' in item ? (item.dstName || item.dstNumber) : undefined
    return dst ?? props.number
  }
  return (item.srcName || item.srcNumber) ?? props.number
})
</script>
